/**
 * useDataScheduler Hook
 *
 * A top-level hook that schedules automatic refresh of reference data at regular intervals.
 *
 * Responsibilities:
 * - Check if reference data needs refresh (based on configured interval since lastUpdatedAt)
 * - Call loadAllData with skipWorkerApis=true to fetch only reference data
 * - Skip if offline, not authenticated, or data is fresh
 * - Return loading state for initial page load (show loader when data is missing/stale)
 * - Background refreshes happen silently without showing loader
 *
 * This prevents redundant data fetches on page refresh while keeping reference data up to date.
 */

import { useEffect, useRef, useMemo } from 'react';
import { useAppSelector } from 'modules/state-mgmt/store';
import useLoadAllData from './useLoadAllData';

// Configurable refresh interval (in minutes)
const REFRESH_INTERVAL_MINUTES = 15;
const REFRESH_INTERVAL_MS = REFRESH_INTERVAL_MINUTES * 60 * 1000;

// Check interval - how often to check if refresh is needed (1 minute is lightweight)
const CHECK_INTERVAL_MS = 60 * 1000; // 1 minute

const useDataScheduler = () => {
  const { isOnline, userId, lastUpdatedAt, projectId, employersRaw } = useAppSelector(state => ({
    isOnline: state.app.isOnline,
    userId: state.user?.userId,
    lastUpdatedAt: state.cache.lastUpdatedAt,
    projectId: state.onboarding.temp?.project?.value,
    employersRaw: state.onboarding.temp?.employers || [],
  }));

  // Serialize employers array to avoid array reference issues in dependencies
  const employersKey = useMemo(() => employersRaw.map?.(e => e.value).join(',') || '', [employersRaw]);

  const [{ isLoading, errors }, loadAllData] = useLoadAllData();

  // Track the lastUpdatedAt value and staleness when this hook first mounted
  // This helps us distinguish "initial page load with stale data" from "background refresh"
  const initialLastUpdatedAt = useRef<string | null | undefined>(undefined);
  const wasDataStaleAtMount = useRef<boolean | null>(null);

  const isDataStale = useMemo(() => {
    if (!lastUpdatedAt) return true;
    const now = Date.now();
    const lastUpdate = new Date(lastUpdatedAt).getTime();
    const timeSinceLastUpdate = now - lastUpdate;
    return timeSinceLastUpdate >= REFRESH_INTERVAL_MS;
  }, [lastUpdatedAt]);

  // Capture initial values only once on first render
  if (initialLastUpdatedAt.current === undefined) {
    initialLastUpdatedAt.current = lastUpdatedAt;
    wasDataStaleAtMount.current = isDataStale;
  }

  // Calculate if we should show loader
  // Show loader ONLY when:
  // - Data was stale/missing when we mounted (wasDataStaleAtMount = true) AND
  // - Data hasn't been refreshed yet this session (lastUpdatedAt === initialLastUpdatedAt) AND
  // - User is authenticated AND online
  //
  const isLoadingData = useMemo(() => {
    // No auth - allow routes to render for login redirect
    if (!userId || !projectId) {
      return false;
    }

    // Offline - can't fetch anyway, use cached data, don't show loader
    if (!isOnline) {
      return false;
    }

    // Data was fresh when we mounted - never show loader (background refreshes are silent)
    if (wasDataStaleAtMount.current === false) {
      return false;
    }

    if (!isLoading && errors.length > 0) {
      return false;
    }

    // Data was stale at mount - show loader until it updates
    // Once lastUpdatedAt changes, initial load is complete
    if (lastUpdatedAt === initialLastUpdatedAt.current) {
      return true; // Still waiting for initial refresh to complete
    }

    return false; // Initial refresh completed
  }, [userId, projectId, isOnline, lastUpdatedAt, isLoading, errors]);

  // Refs are necessary to avoid recreating the interval when these values change
  // The interval callback needs the latest values but we don't want to restart the interval
  const lastUpdatedAtRef = useRef(lastUpdatedAt);
  const loadAllDataRef = useRef(loadAllData);
  lastUpdatedAtRef.current = lastUpdatedAt;
  loadAllDataRef.current = loadAllData;

  // Set up periodic data refresh check
  useEffect(() => {
    const employers = employersKey.split(',').filter(Boolean);

    const checkAndRefreshData = () => {
      // Skip if offline or not authenticated
      if (!isOnline || !userId) {
        return;
      }

      // Check if data needs refresh using refs to get latest values
      const now = Date.now();
      const currentLastUpdatedAt = lastUpdatedAtRef.current;
      const lastUpdate = currentLastUpdatedAt ? new Date(currentLastUpdatedAt).getTime() : 0;
      const timeSinceLastUpdate = now - lastUpdate;

      // Refresh if data is older than configured interval or never been fetched
      if (timeSinceLastUpdate >= REFRESH_INTERVAL_MS || !currentLastUpdatedAt) {
        loadAllDataRef.current({
          skipWorkerApis: true,
          projectId,
          employers,
          onlyImportantApis: false,
        });
      }
    };

    // Run check immediately on mount or when user/project context changes
    checkAndRefreshData();

    // Then check every minute (lightweight timestamp comparison)
    const intervalId = setInterval(checkAndRefreshData, CHECK_INTERVAL_MS);

    // Cleanup on unmount or when dependencies change
    return () => clearInterval(intervalId);

    // NOTE: lastUpdatedAt is intentionally NOT in dependencies
    // We use lastUpdatedAtRef to access it without restarting the interval
  }, [isOnline, userId, projectId, employersKey]);

  return { isLoadingData };
};

export default useDataScheduler;
